/**
 * Arrow Navigation
 *
 * Provides keyboard arrow navigation for navbar and mobile menu.
 * Handles ArrowLeft/Right in desktop navbar and ArrowUp/Down in mobile menu,
 * plus Home/End/Escape keys for complete keyboard accessibility.
 *
 * Features:
 * - Desktop: ArrowLeft/Right to navigate navbar items
 * - Mobile menu: ArrowUp/Down, Home/End navigation
 * - Skips hidden elements and inactive dropdowns
 * - Respects inert state for collapsed accordions
 * - Escape to close mobile menu
 * - ArrowLeft/Right to collapse/expand mobile dropdowns
 */

import { getSelector, globalConfig, querySelector } from '@utils';

interface EventHandler {
  element: Element | Document;
  event: string;
  handler: EventListener;
  options?: AddEventListenerOptions;
}

interface ModuleConfig {
  attributes: {
    elements: Record<string, { primary: string; aliases?: string[] }>;
  };
}

interface NavbarConfig {
  attributes: {
    elements: {
      wrapper: { primary: string; aliases?: string[] };
      'skip-link': { primary: string; aliases?: string[] };
    };
  };
  menu: ModuleConfig;
}

export function init(config: ModuleConfig, navbarConfig?: NavbarConfig) {
  const cleanup = {
    handlers: [] as EventHandler[],
  };

  const addHandler = (
    element: Element | Document,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };

  function setupDesktopArrowNavigation(
    addHandler: (
      element: Element | Document,
      event: string,
      handler: EventListener,
      options?: AddEventListenerOptions
    ) => void
  ): void {
    const keydownHandler = (e: KeyboardEvent): void => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      const menu = navbarConfig
        ? querySelector(navbarConfig.menu as any, 'wrapper')
        : document.querySelector('[data-hs-nav-menu="wrapper"]');
      if (menu && menu.contains(document.activeElement)) return;

      const navbar = navbarConfig
        ? querySelector(navbarConfig as any, 'wrapper')
        : document.querySelector('[data-hs-nav="wrapper"]');

      if (!navbar || !navbar.contains(document.activeElement)) return;

      // Check if focus is inside an open dropdown (using .is-active class)
      const openDropdown = navbar.querySelector(
        `.${globalConfig.classes.active}[data-hs-nav-dropdown]`
      );
      if (openDropdown && openDropdown.contains(document.activeElement)) return;

      e.preventDefault();

      const clickableSelector = getSelector(globalConfig.clickable as any, 'button');
      const allNavbarElements = Array.from(navbar.querySelectorAll(clickableSelector));
      const focusableElements = Array.from(allNavbarElements).filter((el) => {
        if (el.getAttribute('tabindex') === '-1') return false;

        const isInDropdownList = el.closest('[role="menu"]');
        if (isInDropdownList) return false;

        const menuSelector = navbarConfig
          ? navbarConfig.menu.attributes.elements.wrapper.primary.split('=')[0]
          : 'data-hs-nav-menu';
        const isInMenu = el.closest(`[${menuSelector}="wrapper"]`);
        if (isInMenu) return false;

        const skipSelector = navbarConfig
          ? navbarConfig.attributes.elements['skip-link'].primary.split('=')[0]
          : 'data-hs-nav';
        const isInSkipLink = el.closest(`[${skipSelector}="skip-link"]`);
        if (isInSkipLink) return false;

        const computedStyle = window.getComputedStyle(el);
        const htmlEl = el as HTMLElement;
        const isHidden =
          computedStyle.display === 'none' ||
          computedStyle.visibility === 'hidden' ||
          computedStyle.opacity === '0' ||
          htmlEl.offsetWidth === 0 ||
          htmlEl.offsetHeight === 0;
        if (isHidden) return false;

        let parent = el.parentElement;
        while (parent && parent !== navbar) {
          const parentStyle = window.getComputedStyle(parent);
          const parentHtmlEl = parent as HTMLElement;
          const parentHidden =
            parentStyle.display === 'none' ||
            parentStyle.visibility === 'hidden' ||
            parentHtmlEl.offsetWidth === 0 ||
            parentHtmlEl.offsetHeight === 0;
          if (parentHidden) return false;
          parent = parent.parentElement;
        }

        return true;
      });

      const currentIndex = focusableElements.indexOf(document.activeElement as Element);
      if (currentIndex === -1) return;

      if (e.key === 'ArrowRight') {
        if (currentIndex < focusableElements.length - 1) {
          const nextIndex = currentIndex + 1;
          (focusableElements[nextIndex] as HTMLElement).focus();
        }
      } else {
        if (currentIndex > 0) {
          const nextIndex = currentIndex - 1;
          (focusableElements[nextIndex] as HTMLElement).focus();
        }
      }
    };

    addHandler(document, 'keydown', keydownHandler);
  }

  function setupMenuArrowNavigation(
    addHandler: (
      element: Element | Document,
      event: string,
      handler: EventListener,
      options?: AddEventListenerOptions
    ) => void
  ): void {
    const menuContainer = navbarConfig
      ? querySelector(navbarConfig.menu as any, 'wrapper')
      : document.querySelector('[data-hs-nav-menu="wrapper"]');
    if (!menuContainer) return;

    function getFocusableElements(): Element[] {
      const menuClickableSelector = getSelector(globalConfig.clickable as any, 'button');
      const allElements = Array.from(menuContainer.querySelectorAll(menuClickableSelector));
      return Array.from(allElements).filter((el) => {
        // Check if element or any ancestor is inert (inactive dropdown)
        let current: Element | null = el;
        while (current && current !== menuContainer) {
          if ((current as HTMLElement).inert) {
            return false;
          }
          current = current.parentElement;
        }
        return true;
      });
    }

    let currentFocusIndex = -1;

    const keydownHandler = (e: KeyboardEvent): void => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const activeElement = document.activeElement;
      currentFocusIndex = focusableElements.indexOf(activeElement as Element);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentFocusIndex < focusableElements.length - 1) {
          currentFocusIndex = currentFocusIndex + 1;
          (focusableElements[currentFocusIndex] as HTMLElement).focus();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentFocusIndex > 0) {
          currentFocusIndex = currentFocusIndex - 1;
          (focusableElements[currentFocusIndex] as HTMLElement).focus();
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (
          activeElement &&
          (activeElement as HTMLElement).tagName === 'BUTTON' &&
          activeElement.hasAttribute('aria-controls')
        ) {
          const isExpanded = activeElement.getAttribute('aria-expanded') === 'true';
          if (!isExpanded) {
            (activeElement as HTMLElement).click();
          }
          return;
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (
          activeElement &&
          (activeElement as HTMLElement).tagName === 'BUTTON' &&
          activeElement.hasAttribute('aria-controls')
        ) {
          const isExpanded = activeElement.getAttribute('aria-expanded') === 'true';
          if (isExpanded) {
            (activeElement as HTMLElement).click();
          }
          return;
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        currentFocusIndex = 0;
        (focusableElements[0] as HTMLElement).focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        currentFocusIndex = focusableElements.length - 1;
        (focusableElements[focusableElements.length - 1] as HTMLElement).focus();
      } else if (e.key === ' ' && activeElement && (activeElement as HTMLElement).tagName === 'A') {
        e.preventDefault();
      } else if (e.key === 'Escape') {
        const menuButton = navbarConfig
          ? querySelector(navbarConfig.menu as any, 'button')
          : document.querySelector('[data-hs-nav-menu="button"]');
        if (menuButton) {
          (menuButton as HTMLElement).click();
          (menuButton as HTMLElement).focus();
        }
      }
    };

    addHandler(menuContainer, 'keydown', keydownHandler);
  }

  setupDesktopArrowNavigation(addHandler);
  setupMenuArrowNavigation(addHandler);

  return {
    result: 'arrow-navigation initialized',
    destroy: () => {
      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;
    },
  };
}
